import { SignInInput } from '../lib/ui-kit/index.js';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FollowUpForm from '../components/FollowUpForm';
import openNewTabIcon from '../assets/open-new-tab.svg';
import { useTenant } from '../FeatureFlags';
import type { QuestionProTaskStatus } from '../hooks/useQuestionProTask';
import { postMessageChangeStep } from '../utils/postMessage';

const howToFindAmazonPasswordUrl = '/help/find-password';

type SignInPageProps = {
  retailerName: string;
  signinId?: string;
  onStartConnection: () => void;
  onSubmitPreemptiveForm: (credentials: {
    email: string;
    password: string;
  }) => void;
  showFollowUpForm: boolean;
  questionProTaskStatus: QuestionProTaskStatus;
  requiresPassword?: boolean;
  // The password-help page is Amazon-specific, so hosts opt in per retailer.
  showPasswordHelp?: boolean;
  retailerDomain?: string;
  emailInputType?: 'email' | 'text';
  emailPlaceholder?: string;
  onCloseFollowUpForm?: () => void;
  // Optional back handler for hosts that drive their own navigation (e.g. the
  // standalone cleaning-study flow). Defaults to navigate('/connect').
  onBack?: () => void;
  // Fires once the follow-up (2FA/OTP) iframe reports a terminal signin result
  // via postMessage — see FollowUpForm.
  onSigninDone?: (
    status: 'SUCCESS' | 'ERROR',
    error?: string,
    panelEligibility?: { eligible: boolean; redirectUrl: string | null }
  ) => void;
  variant?: 'overlay' | 'inline';
};

export function SignInPage({
  retailerName,
  signinId,
  onStartConnection,
  onSubmitPreemptiveForm,
  showFollowUpForm,
  questionProTaskStatus,
  requiresPassword = true,
  showPasswordHelp = true,
  retailerDomain,
  emailInputType = 'email',
  emailPlaceholder = 'Email',
  onCloseFollowUpForm,
  onBack,
  onSigninDone,
  variant = 'overlay',
}: SignInPageProps) {
  const navigate = useNavigate();
  const { copy } = useTenant();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const canSubmit = requiresPassword ? !!(email && password) : !!email;
  const hasRequestSigninForm = useRef(false);

  const handleClose = () => {
    if (showFollowUpForm && onCloseFollowUpForm) return onCloseFollowUpForm();
    if (onBack) return onBack();
    navigate('/connect');
  };

  // Signal the embedding parent (survey host) that the user reached the
  // sign-in step. Fires on entering /connect/signin, not on connection start.
  useEffect(() => {
    postMessageChangeStep(2);
  }, []);

  useEffect(() => {
    if (!requiresPassword || !password || hasRequestSigninForm.current) return;
    onStartConnection();
    hasRequestSigninForm.current = true;
    // oxlint-disable-next-line react/exhaustive-deps
  }, [password, requiresPassword]);

  const isOverlay = variant === 'overlay';

  return (
    <div
      className={
        isOverlay
          ? 'fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-0 sm:px-6'
          : 'flex-1 flex flex-col items-center justify-center py-12 px-6'
      }
      onMouseDown={
        isOverlay
          ? (e) => {
              if (e.target === e.currentTarget) handleClose();
            }
          : undefined
      }
    >
      <div
        className={
          isOverlay ? 'w-full sm:max-w-[420px]' : 'w-full max-w-[480px]'
        }
      >
        {questionProTaskStatus === 'invalid' && (
          <div className="mx-4 sm:mx-0 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 mb-3">
            <p className="font-medium">We couldn&apos;t verify your link.</p>
            <p className="mt-1">
              Your QuestionPro connection appears to be invalid. Please try
              again or contact support.
            </p>
          </div>
        )}

        <div
          role={isOverlay ? 'dialog' : undefined}
          aria-modal={isOverlay ? true : undefined}
          onMouseDown={isOverlay ? (e) => e.stopPropagation() : undefined}
          className={`bg-surface border border-rule shadow-[0_4px_24px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.04)] ${
            isOverlay ? 'rounded-t-2xl sm:rounded-2xl' : 'rounded-2xl'
          }`}
        >
          {isOverlay && (
            <div className="sm:hidden w-9 h-1 rounded-full bg-rule mx-auto mt-2.5 mb-1" />
          )}

          <div className="relative flex flex-col items-center px-6 pt-4 pb-5 border-b border-rule-light">
            <svg
              className="w-8 h-8 text-ink mb-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M12 1.5l8 3v6c0 5.1-3.4 9.6-8 11-4.6-1.4-8-5.9-8-11v-6l8-3z" />
              <path d="M9.5 10.5V9a2.5 2.5 0 015 0v1.5M9 10.5h6v4.5H9v-4.5z" />
            </svg>

            <h2 className="font-display font-bold text-[18px] text-ink tracking-[-0.01em]">
              Sign in to {retailerName}
            </h2>
          </div>

          <div className="p-6">
            {!showFollowUpForm ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!canSubmit) return;
                  if (!requiresPassword) {
                    onStartConnection();
                  }
                  onSubmitPreemptiveForm({ email, password });
                }}
                className="space-y-3"
              >
                {retailerDomain && (
                  <div className="w-full rounded-xl bg-paper px-4 py-3.5 text-[14px] text-ink-muted">
                    https://www.{retailerDomain}
                  </div>
                )}
                <SignInInput
                  id="email"
                  data-testid="signin-email-input"
                  type={emailInputType}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={emailPlaceholder}
                />
                {requiresPassword && (
                  <SignInInput
                    id="password"
                    data-testid="signin-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                  />
                )}

                <button
                  type="submit"
                  data-testid="signin-connect-button"
                  disabled={!canSubmit}
                  className={`w-full py-3.5 bg-primary hover:bg-primary-deep text-white text-[15px] font-semibold rounded-xl transition-colors mt-1 ${
                    !canSubmit ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Link account
                </button>

                {requiresPassword && showPasswordHelp && (
                  <div className="text-center pt-1">
                    <a
                      data-testid="signin-password-help-link"
                      href={howToFindAmazonPasswordUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[14px] text-primary hover:underline font-medium"
                    >
                      How to find your Amazon password
                      <img
                        src={openNewTabIcon}
                        alt=""
                        className="w-[13px] h-[13px]"
                      />
                    </a>
                  </div>
                )}
              </form>
            ) : (
              <FollowUpForm signinId={signinId} onDone={onSigninDone} />
            )}
          </div>
        </div>
        {copy.signinSecurityNote && (
          <p className="text-center text-[12px] text-ink-faint mt-4 mb-4 sm:mb-0 leading-[1.5]">
            {copy.signinSecurityNote}
          </p>
        )}
      </div>
    </div>
  );
}
